// All dates and times are displayed in Azerbaijan / Baku timezone (UTC+4)
const BAKU_TZ = 'Asia/Baku';

const AZ_MONTHS = [
    'Yanvar','Fevral','Mart','Aprel','May','İyun',
    'İyul','Avqust','Sentyabr','Oktyabr','Noyabr','Dekabr'
];

/**
 * Convert any date-like value to a Date object with UTC interpretation
 * for MySQL datetime strings that have no timezone suffix.
 * e.g. "2026-05-19 20:30:00" → treated as UTC → displayed in Baku (+4)
 */
const toDate = (str) => {
    if (!str) return null;
    const s = String(str).trim();
    // MySQL format "YYYY-MM-DD HH:MM:SS" or "YYYY-MM-DDTHH:MM:SS" without offset → add Z
    const normalized = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(s) && !/Z|[+-]\d{2}:?\d{2}$/.test(s)
        ? s.replace(' ', 'T') + 'Z'
        : s;
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
};

/** "2026-05-19" — today in Baku timezone (use instead of toISOString which gives UTC) */
export const todayISO = () =>
    new Date().toLocaleDateString('en-CA', { timeZone: BAKU_TZ });

/** "19 May" — from date-only string YYYY-MM-DD */
export const fmtDate = (str) => {
    if (!str) return '—';
    const parts = String(str).slice(0, 10).split('-');
    if (parts.length < 3) return String(str);
    return `${parseInt(parts[2], 10)} ${AZ_MONTHS[parseInt(parts[1], 10) - 1]}`;
};

/** "19 May 2026" — same but with year */
export const fmtDateFull = (str) => {
    if (!str) return '—';
    const parts = String(str).slice(0, 10).split('-');
    if (parts.length < 3) return String(str);
    return `${parseInt(parts[2], 10)} ${AZ_MONTHS[parseInt(parts[1], 10) - 1]} ${parts[0]}`;
};

/** "19 May, Saat 20:30" — from any ISO / MySQL datetime string, displayed in Baku time */
export const fmtDateTime = (str) => {
    if (!str) return '—';
    const d = toDate(str);
    if (!d) return String(str);
    const fmt = new Intl.DateTimeFormat('en', {
        timeZone: BAKU_TZ,
        day: 'numeric', month: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const parts = Object.fromEntries(fmt.formatToParts(d).map(p => [p.type, p.value]));
    const day   = parseInt(parts.day,   10);
    const month = AZ_MONTHS[parseInt(parts.month, 10) - 1];
    return `${day} ${month}, Saat ${parts.hour}:${parts.minute}`;
};

/** "24.06.2026 Saat 23:11" — full numeric date + time, Baku timezone */
export const fmtDateTimeNum = (str) => {
    if (!str) return '—';
    const d = toDate(str);
    if (!d) return String(str);
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
        timeZone: BAKU_TZ,
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d).map(x => [x.type, x.value]));
    return `${p.day}.${p.month}.${p.year} Saat ${p.hour}:${p.minute}`;
};

/** "16.09.2026 23:11" — kompakt numerik tarix+saat (mətbəx çeki üçün, dar kağızda sığsın) */
export const fmtDateTimeNumShort = (str) => {
    if (!str) return '—';
    const d = toDate(str);
    if (!d) return String(str);
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
        timeZone: BAKU_TZ,
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d).map(x => [x.type, x.value]));
    return `${p.day}.${p.month}.${p.year} ${p.hour}:${p.minute}`;
};

/** "40 dəq əvvəl" — relative time from now (dynamic). */
export const fmtAgo = (str) => {
    if (!str) return '';
    const d = toDate(str);
    if (!d) return '';
    const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
    if (mins < 1) return 'indicə';
    if (mins < 60) return `${mins} dəq əvvəl`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h} saat ${m} dəq əvvəl` : `${h} saat əvvəl`;
};

/** "20:30" — time only, displayed in Baku timezone */
export const fmtTime = (str) => {
    if (!str) return '—';
    const d = toDate(str);
    if (!d) return String(str);
    return d.toLocaleTimeString('en-GB', {
        timeZone: BAKU_TZ,
        hour: '2-digit', minute: '2-digit', hour12: false,
    });
};

/** "19 May 2026" — today's date in Azerbaijani, Baku timezone */
export const todayAz = () => {
    const fmt = new Intl.DateTimeFormat('en', {
        timeZone: BAKU_TZ, day: 'numeric', month: 'numeric', year: 'numeric',
    });
    const parts = Object.fromEntries(fmt.formatToParts(new Date()).map(p => [p.type, p.value]));
    return `${parseInt(parts.day)} ${AZ_MONTHS[parseInt(parts.month) - 1]} ${parts.year}`;
};

/**
 * "2026-05-19" — format any Date object as YYYY-MM-DD in Baku timezone.
 * Use this instead of d.toISOString().slice(0,10) everywhere in the app.
 */
export const bakuISO = (d) =>
    d.toLocaleDateString('en-CA', { timeZone: BAKU_TZ });
